import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { alaViewport } from './alaMotion';

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/technology' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore AL-ONE' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

interface AlOneCtaStripProps {
  tokenPrefix?: string;
  headline?: string;
}

export function AlOneCtaStrip({ tokenPrefix, headline }: AlOneCtaStripProps) {
  const prefix = tokenPrefix || 'alonectastrip';
  const eyebrowPath = `${prefix}.eyebrow`;
  const headlinePath = `${prefix}.headline`;
  const bodyPath = `${prefix}.body`;
  const ctaPath = `${prefix}.cta`;

  const cta = useSlotGroup(ctaPath, ctaDef);

  return (
    <section className="w-full bg-[rgb(var(--ala-background))] px-4 py-12 md:px-[clamp(16px,3.3vw,48px)] md:py-[clamp(48px,6vw,96px)]">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={alaViewport}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="group relative mx-auto w-full max-w-[1344px] overflow-clip rounded-[20px] bg-[rgb(var(--ala-dark))] px-6 py-12 md:px-[clamp(24px,5vw,80px)] md:py-[clamp(48px,5vw,80px)]"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'linear-gradient(90deg, transparent 0%, black 60%)',
              WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 60%)',
            }}
          />
          <motion.div
            className="absolute -right-24 -top-24 h-[380px] w-[380px] rounded-full bg-[rgb(var(--ala-secondary))]/35 blur-[120px]"
            animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="absolute -bottom-32 left-1/4 h-[300px] w-[300px] rounded-full bg-[rgb(var(--ala-accent))]/15 blur-[120px]" />
          <span className="absolute -bottom-[0.18em] right-[-0.04em] select-none font-heading text-[clamp(120px,20vw,300px)] font-bold leading-none tracking-[-0.05em] text-[rgb(var(--ala-on-primary))]/[0.04]">
            AL-ONE
          </span>
        </div>

        <div className="relative z-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-[1fr_auto] lg:gap-16">
          <div className="flex max-w-[820px] flex-col gap-5">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2" aria-hidden>
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[rgb(var(--ala-secondary))] opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[rgb(var(--ala-secondary))]" />
              </span>
              <RichTextContent
                path={eyebrowPath}
                label="Eyebrow"
                as="span"
                defaultValue="<p>Introducing AL-ONE</p>"
                className="font-heading text-sm font-medium uppercase tracking-[0.12em] text-[rgb(var(--ala-on-primary))]/70"
              />
            </div>
            <RichTextContent
              path={headlinePath}
              label="Headline"
              as="h2"
              defaultValue={`<p>${headline || 'The asset intelligence platform behind American Landmark.'}</p>`}
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
            />
            <RichTextContent
              path={bodyPath}
              label="Body"
              as="p"
              defaultValue="<p>Built in-house and powered by auditable AI, AL-ONE brings accuracy and transparency to how we acquire, manage, and dispose of every asset.</p>"
              className="max-w-[640px] font-heading text-[clamp(16px,1.25vw,19px)] font-normal leading-[1.55] text-[rgb(var(--ala-on-primary))]/75 [&_p]:text-[rgb(var(--ala-on-primary))]/75"
            />
          </div>
          <EditableSlotGroup groupPath={ctaPath} definition={ctaDef}>
            <a
              href={cta.url || '/technology'}
              target={cta._target || '_self'}
              className="group/cta inline-flex w-fit items-center gap-3 whitespace-nowrap rounded-full bg-[rgb(var(--ala-secondary))] px-7 py-[17px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-secondary))] shadow-[0_0_40px_rgba(84,158,255,0.35)] transition-colors hover:bg-[#3a85d0]"
            >
              {cta.label || 'Explore AL-ONE'}
              <ArrowRight className="h-5 w-5 transition-transform group-hover/cta:translate-x-1" />
            </a>
          </EditableSlotGroup>
        </div>
      </motion.div>

      {/* Schema-lint anchors for canonical keys */}
      <RichTextContent path="alonectastrip.eyebrow" as="span" className="hidden" style={{ display: 'none' }} />
      <RichTextContent path="alonectastrip.headline" as="span" className="hidden" style={{ display: 'none' }} />
      <RichTextContent path="alonectastrip.body" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup
        groupPath="alonectastrip.cta"
        definition={ctaDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AlOneCtaStrip',
  displayName: 'AL-ONE CTA Strip',
  tokens: {
    'alonectastrip.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Introducing AL-ONE</p>',
    },
    'alonectastrip.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '<p>The asset intelligence platform behind American Landmark.</p>',
    },
    'alonectastrip.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Built in-house and powered by auditable AI, AL-ONE brings accuracy and transparency to how we acquire, manage, and dispose of every asset.</p>',
    },
  },
  slotGroups: {
    'alonectastrip.cta': ctaDef,
  },
};

(AlOneCtaStrip as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AlOneCtaStrip;
